import { useRouter } from "next/navigation";
import { useUser } from '@/components/widgets/UserProvider'
import React, { useState, useRef } from "react";

interface ScrollImageProps {
  primaryImage: string
  hoverImage: string
  mobileImage: string
  text: string
  onClick?: (text: string) => void
  page?: string
}

interface GalleryImage {
  primaryImage: string;
  mobileImage: string;
  hoverImage: string;
  text: string;
  onClick: () => void
}

interface ShopByShapeProps {
  page?: string
  onShapeClick?: (shape: string) => void
  shapeNames?: string[]
}

const ScrollImage: React.FC<ScrollImageProps> = ({
  primaryImage,
  hoverImage,
  mobileImage,
  text,
  onClick,
  page,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(text)
    }
  }

  const getContainerClasses = () => {
    if (page === 'product-details') {
      return 'w-[70px] sm:w-[80px] flex flex-col items-center justify-center sm:mx-2 sm:mx-0'
    }
    return 'w-[100px] sm:w-[150px] flex flex-col items-center justify-center flex-shrink-0 sm:mx-4 sm:mx-0'
  }

  const getImageClasses = () => {
    if (page === 'product-details') {
      return {
        container:
          'w-[56px] h-[56px] sm:w-[80px] sm:h-[80px] relative group flex justify-center items-center select-none',
        image: 'w-[40px] h-[40px] sm:w-full sm:h-full',
      }
    }
    return {
      container:
        'w-[56px] h-[56px] sm:w-[144px] sm:h-[144px] relative group flex justify-center select-none',
      image: 'w-[56px] h-[56px] sm:w-full sm:h-full',
    }
  }

  const getTextClasses = () => {
    if (page === 'product-details') {
      return 'font-barlowNormal text-[8px] sm:text-[10px] tracking-[0.5px] uppercase text-[#F2EFDE] sm:mt-1 transition-all duration-300 ease-in-out md:group-hover:text-[#A08260] text-center whitespace-nowrap select-none'
    }
    return 'font-barlowNormal text-[10px] sm:text-[16px] tracking-[2px] uppercase text-white mt-4 sm:mt-2 transition-all duration-300 ease-in-out md:group-hover:text-[#A08260] text-center whitespace-nowrap select-none'
  }

  const classes = getImageClasses()

  return (
    <div
      className={getContainerClasses()}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <div className={classes.container}>
        <img
          draggable='false'
          src={primaryImage}
          alt={text}
          className={`block object-cover ${classes.image} md:transition-all md:duration-300 md:ease-in-out md:transform md:group-hover:opacity-0 md:group-hover:scale-95`}
        />
        <img
          draggable='false'
          src={hoverImage}
          alt={`${text}`}
          className={`object-cover rounded-full absolute top-0 left-0 w-full h-full
                   hidden md:block
                   md:opacity-0 md:scale-90
                   md:transition-all md:duration-500 md:ease-in-out md:transform
                   md:group-hover:scale-100 md:group-hover:opacity-100`}
        />
        <div className='absolute inset-0 bg-black opacity-0 md:group-hover:opacity-20 transition-opacity duration-300 rounded-full hidden md:block' />
      </div>
      <p className={getTextClasses()}>{text}</p>
    </div>
  )
}

const ShopByShape: React.FC<ShopByShapeProps> = ({
  page = 'engagement',
  onShapeClick,
  shapeNames,
}) => {
  const router = useRouter()
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const { userPageLoading } = useUser()

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0))
    setScrollLeft(scrollContainerRef.current?.scrollLeft || 0)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()
    const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0)
    const walk = (x - startX) * 2
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollLeft - walk
    }
  }

  const getAllImages = (): GalleryImage[] => {
    if (page === 'engagement' || page === 'product-details') {
      return [
        {
          primaryImage:
            'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/1st_icon.png',
          mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Round_cut.png',
          hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Round_cut_image.png',
          text: 'Round Cut',
          onClick: () =>
            router.push('/product-listing?shape=Round'),
        },
        {
          primaryImage:
            'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/2nd_icon.png',
          mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Emerald_cut.png',
          hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Emerald_cut_image.png',
          text: 'Emerald Cut',
          onClick: () =>
            router.push('/product-listing?shape=Emerald'),
        },
        {
          primaryImage:
            'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/7th_icon.png',
          mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Cushion_cut.png',
          hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Cushion_cut_image.png',
          text: 'Cushion Cut',
          onClick: () =>
            router.push('/product-listing?shape=Cushion'),
        },
        // {
        //   primaryImage:
        //     'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/9th_icon.png',
        //   mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Asscher_cut.png',
        //   hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Asscher_cut_image.png',
        //   text: 'Asscher Cut',
        //   onClick: () =>
        //     router.push('/product-listing?shape=Asscher'),
        // },
        {
          primaryImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/10th_icon.png',
          mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Baguette_cut.png',
          hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Baguette_cut_image.png',
          text: 'Baguette Cut',
          onClick: () =>
            router.push('/product-listing?shape=Baguette'),
        },
        {
          primaryImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/3rd_icon.png',
          mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Marquise_cut.png',
          hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Marquise_cut_image.png',
          text: 'Marquise Cut',
          onClick: () =>
            router.push('/product-listing?shape=Marquise'),
        },
        {
          primaryImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/4th_icon.png',
          mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Oval_cut.png',
          hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Oval_cut_image.png',
          text: 'Oval Cut',
          onClick: () =>
            router.push('/product-listing?shape=Oval'),
        },
        {
          primaryImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/5th_icon.png',
          mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Pear_cut.png',
          hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Pear_cut_image.png',
          text: 'Pear Cut',
          onClick: () =>
            router.push('/product-listing?shape=Pear'),
        },
        {
          primaryImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/8th_icon.png',
          mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Heart_cut.png',
          hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Heart_cut_image.png',
          text: 'Heart Cut',
          onClick: () =>
            router.push('/product-listing?shape=Heart'),
        },
        {
          primaryImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/6th_icon.png',
          mobileImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Princess_cut.png',
          hoverImage: 'https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Product_Cuts/Princess_cut_image.png',
          text: 'Princess Cut',
          onClick: () =>
            router.push('/product-listing?shape=Princess'),
        },
      ]
    }
    return []
  }

  const getParentClasses = () => {
    const baseClasses =
      'overflow-x-auto [&::-webkit-scrollbar]:hidden cursor-grab active:cursor-grabbing select-none sm:max-w-full'

    if (page === 'product-details') {
      return `${baseClasses} sm:-ml-4 sm:pl:0 -mt-2`
    }

    return `${baseClasses} sm:px-4 sm:mx-0 sm:px-0`
  }

  const getChildClasses = () => {
    const baseClasses = 'flex min-w-max sm:min-w-0 '

    if (page === 'product-details') {
      return `${baseClasses} sm:justify-start gap-0 sm:gap-2 `
    }

    return `${baseClasses} sm:justify-between `
  }

  const getFilteredImages = (): GalleryImage[] => {
    const allImages = getAllImages()

    // If shapeNames is empty or undefined, return all images
    if (!shapeNames || shapeNames.length === 0) {
      return allImages
    }

    // Filter images based on shape names
    return allImages.filter((image) =>
      shapeNames.some((shapeName) => {
        return image.text.toLowerCase() == shapeName.toLowerCase()
      })
    )
  }

  const images = getFilteredImages()

  if (page == 'product-details' && !shapeNames) {
    return null
  }

  return (
    <div className='w-full mx-auto mt-4 sm:ml-4 '>
      <div
        ref={scrollContainerRef}
        className={getParentClasses()}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        <div className={getChildClasses()}>
          {images.map((image, index) => {
            return (
            <ScrollImage
              key={index}
              primaryImage={image.primaryImage}
              hoverImage={image.hoverImage}
              mobileImage={image.mobileImage}
              text={image.text}
              onClick={image.onClick}
              page={page}
            />
          )})}
        </div>
      </div>
    </div>
  )
}

export default ShopByShape
